/**
 * AIWorkflowGenerator — Modal for AI-powered workflow creation from documents.
 *
 * Upload a doc/docx/pdf/txt → LLM converts it to workflow JSON → loads into designer.
 *
 * Usage in ChatbotDesigner:
 *   import AIWorkflowGenerator from './AIWorkflowGenerator';
 *   <AIWorkflowGenerator
 *     isOpen={aiModalOpen}
 *     onClose={() => setAiModalOpen(false)}
 *     onGenerated={(workflow) => { // load into designer }}
 *   />
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Bot, Upload, X, Sparkles, FileText, AlertCircle,
  CheckCircle2, Loader2, Settings, ChevronDown
} from 'lucide-react';
import { apiCall } from './Api';

const ALLOWED_EXTENSIONS = ['.txt', '.doc', '.docx', '.pdf', '.xls', '.xlsx', '.csv'];
const MAX_FILE_SIZE_MB = 5;

const AIWorkflowGenerator = ({ isOpen, onClose, onGenerated }) => {
  const [file, setFile] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [channel, setChannel] = useState('whatsapp');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [aiConfig, setAiConfig] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const fileInputRef = useRef(null);

  // Load AI config on open
  useEffect(() => {
    if (isOpen) {
      loadAiConfig();
      // Reset state
      setFile(null);
      setInstructions('');
      setError(null);
      setResult(null);
      setGenerating(false);
    }
  }, [isOpen]);

  const loadAiConfig = async () => {
    try {
      const resp = await apiCall('/api/chatbot/workflows/ai/config');
      if (resp.ok) {
        const data = await resp.json();
        setAiConfig(data);
      }
    } catch (e) {
      console.warn('Could not load AI config:', e);
    }
  };

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile) validateAndSetFile(droppedFile);
  }, []);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (selected) validateAndSetFile(selected);
  };

  const validateAndSetFile = (f) => {
    setError(null);
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`Unsupported file type "${ext}". Use: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`File too large (${(f.size / 1024 / 1024).toFixed(1)}MB). Max ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }
    setFile(f);
  };

  const handleGenerate = async () => {
    if (!file) {
      setError('Please upload a document first.');
      return;
    }

    setGenerating(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (instructions.trim()) formData.append('instructions', instructions.trim());
      formData.append('channel', channel);

      const resp = await apiCall('/api/chatbot/workflows/ai/generate', {
        method: 'POST',
        body: formData,
        headers: { 'Content-Type': undefined },
        timeout: 5 * 60 * 1000, // 5 minutes — local LLMs are slow
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || `Generation failed (${resp.status})`);
      }

      const data = await resp.json();
      setResult(data);
    } catch (e) {
      setError(e.message || 'AI generation failed. Check LLM backend connectivity.');
    } finally {
      setGenerating(false);
    }
  };

  const handleLoadWorkflow = () => {
    if (result?.workflow) {
      onGenerated(result.workflow);
      onClose();
    }
  };

  if (!isOpen) return null;

  const backendStatus = aiConfig?.status;
  const isBackendReady = backendStatus === 'connected' || backendStatus === 'configured';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-violet-50 to-blue-50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-violet-100 rounded-lg">
              <Sparkles className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">AI Workflow Generator</h2>
              <p className="text-xs text-gray-500">Upload a document to auto-create a chatbot workflow</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Backend status */}
          {aiConfig && (
            <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm ${
              isBackendReady
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}>
              {isBackendReady
                ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                : <AlertCircle className="h-4 w-4 flex-shrink-0" />
              }
              <span>
                {aiConfig.backend === 'ollama' && `Ollama (${aiConfig.model}) — ${aiConfig.status}`}
                {aiConfig.backend === 'llamacpp' && `llama.cpp (${aiConfig.model}) — ${aiConfig.status}`}
                {aiConfig.backend === 'anthropic' && `Claude (${aiConfig.model}) — ${aiConfig.status}`}
              </span>
            </div>
          )}

          {/* File upload area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Document
            </label>
            <div
              onDrop={handleFileDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                file
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300 hover:border-violet-400 hover:bg-violet-50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_EXTENSIONS.join(',')}
                onChange={handleFileSelect}
                className="hidden"
              />
              {file ? (
                <div className="flex items-center justify-center space-x-3">
                  <FileText className="h-8 w-8 text-green-500" />
                  <div className="text-left">
                    <p className="font-medium text-green-800">{file.name}</p>
                    <p className="text-xs text-green-600">{(file.size / 1024).toFixed(1)} KB — Click to change</p>
                  </div>
                </div>
              ) : (
                <div>
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    Drop a file here or <span className="text-violet-600 font-medium">browse</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Supports: {ALLOWED_EXTENSIONS.join(', ')} (max {MAX_FILE_SIZE_MB}MB)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Additional Instructions <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g. Focus on the appointment booking flow, use buttons for department selection, capture patient details..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
            />
          </div>

          {/* Advanced options */}
          <div>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <Settings className="h-3.5 w-3.5" />
              <span>Advanced Options</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            </button>
            {showAdvanced && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <label className="block text-xs font-medium text-gray-600 mb-1">Target Channel</label>
                <select
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                >
                  <option value="whatsapp">WhatsApp (max 3 buttons per message)</option>
                  <option value="web">Web Widget (unlimited buttons)</option>
                  <option value="telegram">Telegram</option>
                  <option value="instagram">Instagram DM</option>
                  <option value="facebook">Facebook Messenger</option>
                </select>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start space-x-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Result preview */}
          {result && (
            <div className="border border-green-200 rounded-lg bg-green-50 p-4">
              <div className="flex items-center space-x-2 mb-3">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">Workflow Generated!</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <span className="ml-1 font-medium text-gray-800">{result.workflow?.name}</span>
                </div>
                <div>
                  <span className="text-gray-500">Nodes:</span>
                  <span className="ml-1 font-medium text-gray-800">{result.meta?.node_count}</span>
                </div>
                <div>
                  <span className="text-gray-500">Edges:</span>
                  <span className="ml-1 font-medium text-gray-800">{result.meta?.edge_count}</span>
                </div>
                <div>
                  <span className="text-gray-500">Model:</span>
                  <span className="ml-1 font-medium text-gray-800">{result.meta?.model}</span>
                </div>
              </div>
              {result.workflow?.description && (
                <p className="mt-2 text-xs text-gray-600 italic">{result.workflow.description}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <div className="flex space-x-3">
            {!result ? (
              <button
                onClick={handleGenerate}
                disabled={!file || generating || !isBackendReady}
                className="flex items-center space-x-2 px-5 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    <span>Generate Workflow</span>
                  </>
                )}
              </button>
            ) : (
              <>
                <button
                  onClick={() => { setResult(null); setError(null); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Re-generate
                </button>
                <button
                  onClick={handleLoadWorkflow}
                  className="flex items-center space-x-2 px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Load into Designer</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIWorkflowGenerator;
